import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Calendar, Check, X, AlertTriangle, Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Leaves — list + create + decide.
 * FH approves team leaves (own site); Owner/Head approves FH leaves + beyond-quota.
 * On approval → attendance auto-marks ABSENT_ON_LEAVE for each date in range.
 */
export default function LeavesPage() {
    const { user } = useAuth();
    const eff = user?.effective_role || user?.role;
    const canApprove = eff === "OWNER" || eff === "HEAD" || eff === "FACILITY_HEAD";

    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [sites, setSites] = useState([]);
    const [staff, setStaff] = useState([]);
    const [form, setForm] = useState({ staff_id: "", site_id: "", start_date: "", end_date: "", leave_type: "casual", reason: "", cover_note: "" });
    const [busy, setBusy] = useState(false);

    async function load() {
        try {
            const q = statusFilter ? `?status=${statusFilter}` : "";
            const r = await api.get(`/hr/leaves${q}`);
            setRows(r.data || []);
        } catch (e) { toast.error(apiError(e)); }
    }
    async function loadRefs() {
        try {
            const s = await api.get("/sites"); setSites(s.data || []);
        } catch { /* ignore */ }
    }
    async function loadStaffFor(siteId) {
        if (!siteId) { setStaff([]); return; }
        try {
            const r = await api.get(`/sites/${siteId}/roster`);
            setStaff((r.data && r.data.rows) || r.data || []);
        } catch { setStaff([]); }
    }
    useEffect(() => { load(); }, [statusFilter]);
    useEffect(() => { loadRefs(); }, []);
    useEffect(() => { loadStaffFor(form.site_id); }, [form.site_id]);

    async function create() {
        setBusy(true);
        try {
            await api.post("/hr/leaves", form);
            toast.success("Leave request raised.");
            setShowNew(false);
            setForm({ staff_id: "", site_id: "", start_date: "", end_date: "", leave_type: "casual", reason: "", cover_note: "" });
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }

    async function decide(id, decision) {
        const reason = decision === "REJECTED" ? (window.prompt("Reason for rejection?") || "") : "";
        setBusy(true);
        try {
            await api.post(`/hr/leaves/${id}/decide`, { decision, reason });
            toast.success(`Leave ${decision.toLowerCase()}.`);
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6" data-testid="leaves-page">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                    <h2 className="text-xl font-semibold text-[#1E3355] flex items-center gap-2">
                        <Calendar className="w-5 h-5" /> Leaves
                    </h2>
                    <p className="text-xs text-gray-600">Default quota 12 days/year · beyond-quota needs Owner/Head</p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1"
                        data-testid="leave-status-filter"
                    >
                        <option value="">All statuses</option>
                        <option value="PENDING">Pending</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                    </select>
                    <button
                        onClick={() => setShowNew(true)}
                        className="inline-flex items-center gap-1 rounded-full bg-[#E02B2B] text-white text-sm px-3 py-1.5 hover:bg-red-700"
                        data-testid="leave-new-btn"
                    >
                        <Plus className="w-4 h-4" /> New leave
                    </button>
                </div>
            </div>

            {showNew && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4" data-testid="leave-new-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div>
                            <label className="text-xs text-gray-600">Site</label>
                            <select
                                value={form.site_id}
                                onChange={(e) => setForm({ ...form, site_id: e.target.value, staff_id: "" })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                data-testid="leave-site"
                            >
                                <option value="">Select site</option>
                                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-600">Staff</label>
                            <select
                                value={form.staff_id}
                                onChange={(e) => setForm({ ...form, staff_id: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                data-testid="leave-staff"
                            >
                                <option value="">Select staff</option>
                                {staff.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.role}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-600">Type</label>
                            <select
                                value={form.leave_type}
                                onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                data-testid="leave-type"
                            >
                                <option value="casual">Casual</option>
                                <option value="sick">Sick</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-600">Start date</label>
                            <input
                                type="date" value={form.start_date}
                                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                data-testid="leave-start"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-600">End date</label>
                            <input
                                type="date" value={form.end_date}
                                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                data-testid="leave-end"
                            />
                        </div>
                        <div className="sm:col-span-2 lg:col-span-3">
                            <label className="text-xs text-gray-600">Reason</label>
                            <input
                                value={form.reason}
                                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                data-testid="leave-reason"
                            />
                        </div>
                        <div className="sm:col-span-2 lg:col-span-3">
                            <label className="text-xs text-gray-600">Cover arrangement (recommended for contractual posts)</label>
                            <input
                                value={form.cover_note}
                                onChange={(e) => setForm({ ...form, cover_note: e.target.value })}
                                placeholder="e.g. Sukumar to cover reception on 12-14 Jul"
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                data-testid="leave-cover-note"
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <button
                            onClick={create} disabled={busy || !form.staff_id || !form.site_id || !form.start_date || !form.end_date}
                            className="inline-flex items-center gap-1 rounded-full bg-[#1E3355] text-white text-sm px-3 py-1.5 disabled:opacity-50"
                            data-testid="leave-submit"
                        >
                            Submit
                        </button>
                        <button
                            onClick={() => setShowNew(false)}
                            className="text-sm text-gray-600 px-3 py-1.5"
                            data-testid="leave-cancel"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            <div className="fx-table-scroll bg-white rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-gray-600 border-b border-gray-200">
                        <tr>
                            <th className="p-3">Staff</th>
                            <th className="p-3">Site</th>
                            <th className="p-3">Dates</th>
                            <th className="p-3">Days</th>
                            <th className="p-3">Type</th>
                            <th className="p-3">Status</th>
                            <th className="p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan="7" className="p-6 text-center text-xs text-gray-500" data-testid="leave-empty">No leave requests.</td></tr>
                        )}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-b border-gray-100" data-testid={`leave-row-${r.id}`}>
                                <td className="p-3">{r.staff_id}</td>
                                <td className="p-3">{r.site_id}</td>
                                <td className="p-3 text-xs">{r.start_date} → {r.end_date}</td>
                                <td className="p-3">{r.days}</td>
                                <td className="p-3 text-xs uppercase">{r.leave_type}</td>
                                <td className="p-3">
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "APPROVED" ? "bg-emerald-100 text-emerald-700" : r.status === "REJECTED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                                        {r.status}
                                    </span>
                                    {r.beyond_quota && (
                                        <span className="ml-1 text-xs text-amber-800 inline-flex items-center gap-0.5" title="Beyond annual quota">
                                            <AlertTriangle className="w-3 h-3" /> beyond quota
                                        </span>
                                    )}
                                </td>
                                <td className="p-3">
                                    {r.status === "PENDING" && canApprove && (
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => decide(r.id, "APPROVED")}
                                                className="text-xs px-2 py-0.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"
                                                data-testid={`leave-approve-${r.id}`}
                                            ><Check className="w-3 h-3" /> Approve</button>
                                            <button
                                                onClick={() => decide(r.id, "REJECTED")}
                                                className="text-xs px-2 py-0.5 rounded-full bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1"
                                                data-testid={`leave-reject-${r.id}`}
                                            ><X className="w-3 h-3" /> Reject</button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
